/*
 * Consent-gated third-party script loader.
 *
 * Reads window.AC_CONSENT_CONFIG (defined by snippets/consent-config.liquid)
 * and injects each entry only when ALL of its required Shopify Customer
 * Privacy consent signals are allowed. All region/consent logic is delegated
 * to Shopify's Customer Privacy API; this file only reads it.
 *
 * Fail-closed: if the API is unavailable, no gated scripts load.
 */
(function () {
  'use strict';

  var loaded = {};

  // Debug logging toggle. When true, logs every step — API readiness, the full
  // registered config, the live consent state, and each script's load/block
  // decision (with reasons) — to the browser console, grouped per evaluation.
  // IMPORTANT: set this back to false before deploying to production.
  var DEBUG = false;

  var PREFIX = '[consent-loader]';

  function log() {
    if (!DEBUG || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift(PREFIX);
    console.info.apply(console, args);
  }

  function warn(msg) {
    if (window.console) console.warn(PREFIX + ' ' + msg);
  }

  function group(label) {
    if (DEBUG && window.console && console.group) console.group(PREFIX + ' ' + label);
  }

  function groupEnd() {
    if (DEBUG && window.console && console.groupEnd) console.groupEnd();
  }

  function table(data) {
    if (DEBUG && window.console && console.table) console.table(data);
    else log(data);
  }

  function missingSignals(entry, checks) {
    if (!entry.enabled) return ['(disabled)'];
    if (!Array.isArray(entry.consent)) return ['(no consent array)'];
    return entry.consent.filter(function (sig) {
      var check = checks[sig];
      return !(typeof check === 'function' && check() === true);
    });
  }

  function signalChecks() {
    var cp = window.Shopify.customerPrivacy;
    return {
      analytics: function () { return cp.analyticsProcessingAllowed(); },
      marketing: function () { return cp.marketingAllowed(); },
      preferences: function () { return cp.preferencesProcessingAllowed(); },
      sale_of_data: function () { return cp.saleOfDataAllowed(); }
    };
  }

  function isAllowed(entry, checks) {
    if (!entry.enabled) return false;
    if (!Array.isArray(entry.consent) || entry.consent.length === 0) return false;
    return entry.consent.every(function (sig) {
      var check = checks[sig];
      return typeof check === 'function' && check() === true;
    });
  }

  function injectSrc(entry) {
    var s = document.createElement('script');
    s.src = entry.src;
    var attrs = entry.attrs || {};
    Object.keys(attrs).forEach(function (k) {
      if (attrs[k] === true) {
        s.setAttribute(k, '');
      } else if (attrs[k] !== false && attrs[k] != null) {
        s.setAttribute(k, attrs[k]);
      }
    });
    (document.head || document.body).appendChild(s);
    log('   ↳ injected <script src="' + entry.src + '">');
  }

  function inject(entry) {
    try {
      if (entry.type === 'inline' && typeof entry.init === 'function') {
        log('   ↳ running inline init() for "' + entry.id + '"');
        entry.init();
      } else if (entry.type === 'src' && entry.src) {
        injectSrc(entry);
      } else {
        warn('entry "' + entry.id + '" has no runnable src/init — skipped');
      }
    } catch (e) {
      if (window.console) console.error(PREFIX + ' failed to inject ' + entry.id, e);
    }
  }

  function logConfig(config) {
    group(config.length + ' gated script(s) registered');
    table(config.map(function (entry) {
      return {
        id: entry.id,
        label: entry.label || '',
        enabled: entry.enabled !== false,
        type: entry.type,
        requires: (entry.consent || []).join(' + ')
      };
    }));
    groupEnd();
  }

  function evaluate(reason) {
    if (!window.Shopify || !window.Shopify.customerPrivacy) return;
    var config = window.AC_CONSENT_CONFIG || [];
    var checks = signalChecks();
    var state = {
      analytics: checks.analytics(),
      marketing: checks.marketing(),
      preferences: checks.preferences(),
      sale_of_data: checks.sale_of_data()
    };

    group('evaluate — ' + (reason || 'initial'));
    log('current consent state:');
    table(state);

    var justLoaded = [], stillBlocked = [], alreadyLoaded = [];
    config.forEach(function (entry) {
      if (loaded[entry.id]) {
        alreadyLoaded.push(entry.id);
        log('• "' + entry.id + '" — already loaded this page view, skipping');
        return;
      }
      if (!isAllowed(entry, checks)) {
        stillBlocked.push(entry.id);
        log('⛔ blocked "' + entry.id + '" — needs [' +
          (entry.consent || []).join(', ') + '], missing [' +
          missingSignals(entry, checks).join(', ') + ']');
        return;
      }
      log('✅ allowed "' + entry.id + '" (' + (entry.consent || []).join(' + ') + ') — loading now');
      inject(entry);
      loaded[entry.id] = true;
      justLoaded.push(entry.id);
    });

    log('summary → loaded now: [' + justLoaded.join(', ') + '] · ' +
      'blocked: [' + stillBlocked.join(', ') + '] · ' +
      'already loaded: [' + alreadyLoaded.join(', ') + ']');
    groupEnd();
  }

  function onConsentCollected(event) {
    log('event → visitorConsentCollected', (event && event.detail) ? event.detail : '(no detail)');
    evaluate('consent change');
  }

  function onReady(error) {
    if (error || !window.Shopify || !window.Shopify.customerPrivacy) {
      warn('Customer Privacy API unavailable; no gated scripts loaded');
      return; // fail-closed
    }
    log('Customer Privacy API ready.');
    logConfig(window.AC_CONSENT_CONFIG || []);
    evaluate('initial');
    document.addEventListener('visitorConsentCollected', onConsentCollected);
    log('now listening for future consent changes (visitorConsentCollected event).');
  }

  log('init — requesting Shopify Customer Privacy API…');

  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
    warn('Shopify.loadFeatures unavailable; no gated scripts loaded');
    return; // fail-closed
  }

  window.Shopify.loadFeatures(
    [{ name: 'consent-tracking-api', version: '0.1' }],
    onReady
  );
})();
